import { ActionObjectControllerSingleton } from "Controllers";
import { EnvironmentControllerSingleton } from "Controllers/EnvironmentController";
import { SearchPriorityEnum, ToastTypeEnum } from "Enums";
import { ToastHelperSingleton } from "Helpers";
import { ActionObjectLogicSingleton } from "Logics/ActionObjectLogic";
import { EnvironmentLogicSingleton } from "Logics/EnvironmentLogic";
import { SavedQueryContext } from "Providers";
import { FC, useContext } from "react";
import { CaseContext } from "Stores";
import { TActionObjectDTO, TEnvironmentVariableDTO, TSavedQueryTempDTO } from "Types";
import { SearchQueryElementHitCount } from ".";
// styles
import styles from "./searchQueryElementHitCount.module.scss";

export const SearchQueryElementHitCounts: FC = () => {
    // Constants
    const couldNotChangeEnvironmentToastMessage = "Could not change environment variable status.";
    const couldNotChangeActionObjectToastMessage = "Could not change action object status.";

    // Contexts
    const { savedQuery, updateSavedQuery, duplicateSavedQuery } = useContext(SavedQueryContext);
    const { caseState } = useContext(CaseContext);

    const onActionObjectEnableChange = async (elementId: number | string, isEnabled: boolean) => {
        // TODO: deduplicate this code with the one in the ActionObjects component

        if(!savedQuery) return;
        const actionObjectId = elementId as number;
        const newPriority = isEnabled ? SearchPriorityEnum.Should : SearchPriorityEnum.Disabled;
        const foundActionObject: TActionObjectDTO | undefined =
            savedQuery.actionObjects.find((actionObject) => actionObject.id === actionObjectId);

        if (savedQuery.isUserAdded && savedQuery.isOwnedByCurrentUser) {
            const newQuery = {...savedQuery};

            if(!foundActionObject) {
                ToastHelperSingleton.showToast(ToastTypeEnum.Error, couldNotChangeActionObjectToastMessage);
                console.log(`Could not find action object with id: ${actionObjectId}, when changing checkbox in sidebar.`);
                return;
            }
            foundActionObject.searchPriority = newPriority
            const isSuccess = await ActionObjectControllerSingleton.updateAsync(foundActionObject, true);
            if(!isSuccess) {
                ToastHelperSingleton.showToast(ToastTypeEnum.Error, couldNotChangeActionObjectToastMessage);
                console.log(`Could not update action object on server with id: ${actionObjectId}, when changing checkbox in sidebar.`);
                return;
            }

            updateSavedQuery(newQuery);
        } else {
            duplicateSavedQuery(caseState.selectedOriginalTechnologyId, caseState.selectedCaseId, async (duplicatedSavedQuery: TSavedQueryTempDTO): Promise<void> => {
                // safety-checks
                if (!foundActionObject || !duplicatedSavedQuery) return;

                const duplicatedActionObject: TActionObjectDTO | undefined =
                    ActionObjectLogicSingleton.findDuplicatedActionObjectInList(foundActionObject.action,
                        foundActionObject.dobject, duplicatedSavedQuery.actionObjects);

                // safety-checks
                if (!duplicatedActionObject) return;

                duplicatedActionObject.searchPriority = newPriority;
                const isSuccess = await ActionObjectControllerSingleton.updateAsync(duplicatedActionObject, true);

                // update action object list in the saved query context
                if (isSuccess) {
                    for (const actionObject of duplicatedSavedQuery.actionObjects) {
                        if (actionObject.id === duplicatedActionObject.id) {
                            actionObject.searchPriority = duplicatedActionObject.searchPriority;
                        }
                    }

                    duplicatedSavedQuery.actionObjects = [...duplicatedSavedQuery.actionObjects];
                    updateSavedQuery(duplicatedSavedQuery);
                }
            });
        }
            
    };

    const onEnvironmentEnableChange = async (elementId: number | string, isEnabled: boolean) => {
        // TODO: deduplicate this code with the one in the EnvironmentVariables component

        if(!savedQuery) return;
        const environmentId = elementId as number;
        const foundEnvironmentVariable: TEnvironmentVariableDTO | undefined =
            savedQuery.environmentVariables.find((environmentVariable) => environmentVariable.id === environmentId);
        const newPriority = isEnabled ? SearchPriorityEnum.Should : SearchPriorityEnum.Disabled;

        if (savedQuery.isUserAdded && savedQuery.isOwnedByCurrentUser) {
            const newQuery = {...savedQuery};

            if(!foundEnvironmentVariable) {
                ToastHelperSingleton.showToast(ToastTypeEnum.Error, couldNotChangeEnvironmentToastMessage);
                console.log(`Could not find environment variable with id: ${environmentId}, when changing checkbox in sidebar.`);
                return;
            }
            foundEnvironmentVariable.searchPriority = newPriority;
            const isSuccess = await EnvironmentControllerSingleton.updateAsync(foundEnvironmentVariable, true);
            if(!isSuccess) {
                ToastHelperSingleton.showToast(ToastTypeEnum.Error, couldNotChangeEnvironmentToastMessage);
                console.log(`Could not update environment variable on server with id: ${environmentId}, when changing checkbox in sidebar.`);
                return;
            }
            
            updateSavedQuery(newQuery);
        } else {
            duplicateSavedQuery(caseState.selectedOriginalTechnologyId, caseState.selectedCaseId, async (duplicatedSavedQuery: TSavedQueryTempDTO): Promise<void> => {
                // safety-checks
                if (!foundEnvironmentVariable || !duplicatedSavedQuery) return;

                const duplicatedEnvironmentVariable: TEnvironmentVariableDTO | undefined =
                    EnvironmentLogicSingleton.findDuplicatedEnvironmentVariableInList(foundEnvironmentVariable.environment,
                        duplicatedSavedQuery.environmentVariables);
                
                // safety-checks
                if (!duplicatedEnvironmentVariable) return;
                
                duplicatedEnvironmentVariable.searchPriority = newPriority;
                const isSuccess = await EnvironmentControllerSingleton.updateAsync(duplicatedEnvironmentVariable, true);

                // update environment variable list in the saved query context
                if (isSuccess) {
                    for (const environmentVar of duplicatedSavedQuery.environmentVariables) {
                        if (environmentVar.id === duplicatedEnvironmentVariable.id) {
                            environmentVar.searchPriority = duplicatedEnvironmentVariable.searchPriority;
                        }
                    }
                    
                    duplicatedSavedQuery.environmentVariables = [...duplicatedSavedQuery.environmentVariables];
                    updateSavedQuery(duplicatedSavedQuery);
                }
            });
        }
    };

    return (
        <>
            {savedQuery ?
                <div className={[styles.searchQueryElementHitCountContainer, styles.scrollbarCustomThin].join(" ")}>
                    {savedQuery.actionObjects.map((actionObject) => {
                        return (
                            <SearchQueryElementHitCount key={`ao-${actionObject.id}`} onEnableChange={onActionObjectEnableChange}
                                isEnabled={actionObject.searchPriority !== SearchPriorityEnum.Disabled}
                                text={`${actionObject.action} ${actionObject.dobject}`} id={actionObject.id}
                                hitCountInCurrentQuery={actionObject.hitCountInCurrentQuery}/>
                        );
                    })}

                    {savedQuery.environmentVariables.map((environment) => {
                        return (
                            <SearchQueryElementHitCount key={`env-${environment.id}`} onEnableChange={onEnvironmentEnableChange} 
                                isEnabled={environment.searchPriority !== SearchPriorityEnum.Disabled} 
                                text={environment.environment} id={environment.id}
                                hitCountInCurrentQuery={environment.hitCountInCurrentQuery}/>
                        );
                    })}
                </div>
                : null}
        </>
    );
};